import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLeads } from '../../../context/LeadContext';
import useUrlTableState from '../../../hooks/useUrlTableState';
import { get } from '../../../api/apiClient';
import {
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  MapPin,
  Calendar,
  Clock,
  Target,
  CheckCircle,
  AlertCircle,
  X,
  Plus,
  Users,
  Phone,
  Mail,
  Building,
  Trash2,
  RefreshCw,
  Upload,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  UserPlus,
  ArrowRight,
  ClipboardList
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import {
  handleSearchInputChange,
  FIELD_LIMITS,
} from '../../../utils/inputValidation';
import { toast, Toaster } from 'react-hot-toast';
import leadScoringService from '../../../api/crm/leads/leadScoringService';
import leadService from '../../../api/crm/leads/leadService';
import assignmentRuleService from '../../../api/crm/assignment-rules/assignmentRuleService';
import { API_ENDPOINTS } from '../../../api/config';
import { useAuth } from '../../../context/AuthContext';
import { USER_ROLES } from '../../../utils/constants';
import PermissionGate from '../../../components/common/PermissionGate';
import { formatIndianAmount } from '../../../utils/amountFormatter';
import BulkUploadModal from '../../../components/common/BulkUploadModal';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { exportToCSV, generateExportFilename, downloadFromBackend } from '../../../utils/exportUtils';
import AssignmentRuleModal from './AssignmentRuleModal';
import ScoringRuleModal from './ScoringRuleModal';
import scoringRuleService from '../../../api/crm/leads/scoringRuleService';
import QualificationCriteriaModal from './QualificationCriteriaModal';
import qualificationCriteriaService from '../../../api/crm/leads/qualificationCriteriaService';
import ConvertLeadModal from './ConvertLeadModal';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';

const LeadListFilters = ({ onViewLead, onEditLead, onAssignLead, onAddLead, onLogActivity }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSalesManager = user?.role === USER_ROLES.SALES_MANAGER;

  // Check if user has permission to run auto qualification (only super admin and sales manager)
  const canModify = user?.role === USER_ROLES.SUPER_ADMIN || isSalesManager;

  const LeadManagementGate = ({ action, children }) => {
    // Sales Executive is NOT allowed to delete
    if (action === 'delete' && user?.role === USER_ROLES.SALES_EXECUTIVE) {
      return null;
    }

    if (isSalesManager) {
      return <>{children}</>;
    }
    return (
      <PermissionGate permission="lead_management" action={action}>
        {children}
      </PermissionGate>
    );
  };

  // Permission-based access control - use granular permissions instead of role checks
  // This allows custom permission assignments beyond just roles
  const location = useLocation();
  const { leads, deleteLead, bulkDeleteLeads, bulkAssignLeads, loading, error, loadLeads, pagination, clearError } = useLeads();
  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    sortBy: 'updatedAt',
    sortOrder: 'desc',
    source: location.state?.filters?.source || 'all',
    status: location.state?.filters?.status || 'all',
    costType: location.state?.filters?.costType || 'all',
    score: location.state?.filters?.score || 'all',
    owner: location.state?.filters?.owner || 'all',
    assignee: location.state?.filters?.assignee || 'all',
    region: location.state?.filters?.region || 'all',
    startDate: location.state?.filters?.startDate || '',
    endDate: location.state?.filters?.endDate || ''
  }), [location.state]);

  const {
    filters,
    searchTerm,
    setSearchTerm,
    updateFilter,
    updatePage,
    updateLimit,
    updateSort,
    resetFilters
  } = useUrlTableState(defaultFilters);

  const sortColumn = filters.sortBy;
  const sortDirection = filters.sortOrder;

  // State for assignee filter options
  const [assigneeOptions, setAssigneeOptions] = useState([]);
  const [loadingAssignees, setLoadingAssignees] = useState(false);
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [filteredAssigneeOptions, setFilteredAssigneeOptions] = useState([]);
  const [openAssigneeDropdown, setOpenAssigneeDropdown] = useState(false);
  const [assigneeDropdownPosition, setAssigneeDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const assigneeDropdownRef = useRef(null);

  const [masterSources, setMasterSources] = useState([]);
  const masterSourcesRef = useRef([]);
  const [masterDocumentTypes, setMasterDocumentTypes] = useState([]);
  const [masterIndustries, setMasterIndustries] = useState([]);
  const [masterDecisionMakers, setMasterDecisionMakers] = useState([]);
  const [masterCategories, setMasterCategories] = useState([]);
  const [masterStatuses, setMasterStatuses] = useState([]);
  const [masterCompanySizes, setMasterCompanySizes] = useState([]);
  const [masterLeadBuckets, setMasterLeadBuckets] = useState([]);
  const [masterBusinessUnits, setMasterBusinessUnits] = useState([]);
  const [masterRevenueTypes, setMasterRevenueTypes] = useState([]);
  const [masterLastActiveChannels, setMasterLastActiveChannels] = useState([]);

  // Selection state
  const [selectedRows, setSelectedRows] = useState(new Set());
  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // State for Assignment rule modal
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [users, setUsers] = useState([]); // All users
  const [filteredUsers, setFilteredUsers] = useState([]); // Search results for rule modal dropdown

  // Scoring Modal State
  const [showScoringModal, setShowScoringModal] = useState(false);
  const [selectedScoring, setSelectedScoring] = useState(null);
  const [isSavingScoring, setIsSavingScoring] = useState(false);
  const [scoringErrors, setScoringErrors] = useState({});
  const [scoringFormData, setScoringFormData] = useState({
    name: '',
    description: '',
    rules: [],
    isActive: true
  });

  const handleSaveScoring = async () => {
    // Basic validation
    const errors = {};
    if (!scoringFormData.name.trim()) errors.name = 'Rule name is required.';
    // Add other validations as needed, similar to LeadScoringQualification
    // For brevity, using basic check or delegating to modal if it handles it (it handles it via props if we pass errors back)
    // Actually the modal relies on us to validat and set errors usually, or it does internal validation.
    // In LeadScoringQualification, handleSaveScoring does the validation.
    // Let's implement minimal validation here or reuse a validator if extracted.

    // Checks from LeadScoringQualification
    if (scoringFormData.name.trim().length === 0) errors.name = "Rule name is required";
    if (scoringFormData.rules.length === 0) errors.rules = "At least one rule is required";

    const totalScore = scoringFormData.rules.reduce((sum, rule) => sum + Number(rule.score), 0);
    // if (totalScore !== 100) errors.rules = `Total score must equal 100 (Current: ${totalScore})`; // Optional check

    if (Object.keys(errors).length > 0) {
      setScoringErrors(errors);
      return;
    }

    setIsSavingScoring(true);
    try {
      // Transform rules to match backend format
      const rulesToSave = scoringFormData.rules.map(rule => ({
        field: rule.field,
        operator: rule.operator || 'equals',
        value: rule.condition || rule.value,
        score: rule.score
      }));

      const ruleData = {
        ...scoringFormData,
        rules: rulesToSave
      };

      const result = await scoringRuleService.createScoringRule(ruleData); // Only create mode here? Or edit too? Assuming create for now based on button "Assignment Rule" pattern.

      if (result.success) {
        toast.success('Scoring rule created successfully!');
        setScoringFormData({
          name: '',
          description: '',
          rules: [],
          isActive: true
        });
        setSelectedScoring(null);
        setShowScoringModal(false);
      } else {
        toast.error(result.error || 'Failed to create scoring rule');
      }
    } catch (error) {

      toast.error('Failed to save scoring rule');
    } finally {
      setIsSavingScoring(false);
    }
  };

  // Criteria Modal State
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [isSavingCriteria, setIsSavingCriteria] = useState(false);
  const [criteriaErrors, setCriteriaErrors] = useState({});
  const [criteriaFormData, setCriteriaFormData] = useState({
    name: '',
    description: '',
    criteria: [],
    isActive: true,
    qualificationLogic: 'all',
    qualificationResult: 'qualified',
    leadTemperature: 'Cold'
  });

  const handleSaveCriteria = async () => {
    // Basic validation
    const newErrors = {};
    if (!criteriaFormData.name.trim()) newErrors.name = 'Criteria name is required.';
    if (criteriaFormData.criteria.length === 0) newErrors.criteria = 'Please add at least one qualifying criterion.';

    if (Object.keys(newErrors).length > 0) {
      setCriteriaErrors(newErrors);
      return;
    }

    setIsSavingCriteria(true);

    try {
      // Clean up data before sending
      const criteriaToSave = {
        ...criteriaFormData,
        // Ensure numeric fields if any? (Not needed for current fields)
        // Map 'role' to 'designation' if needed inside criteria array?
        criteria: criteriaFormData.criteria.map(c => ({
          ...c,
          field: c.field === 'role' ? 'designation' : c.field
        }))
      };

      let result;
      if (selectedCriteria) {
        result = await qualificationCriteriaService.updateQualificationCriteria(selectedCriteria._id || selectedCriteria.id, criteriaToSave);
      } else {
        result = await qualificationCriteriaService.createQualificationCriteria(criteriaToSave);
      }

      if (result.success) {
        toast.success(selectedCriteria ? 'Criteria updated successfully' : 'Criteria created successfully');
        setCriteriaFormData({
          name: '',
          description: '',
          criteria: [],
          isActive: true,
          qualificationLogic: 'all',
          qualificationResult: 'qualified',
          leadTemperature: 'Cold'
        });
        setSelectedCriteria(null);
        setShowCriteriaModal(false);
      } else {
        toast.error(result.error || 'Failed to save criteria');
      }
    } catch (error) {

      toast.error('Failed to save criteria');
    } finally {
      setIsSavingCriteria(false);
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    if (filters.sortBy === column) {
      updateSort(column, filters.sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      updateSort(column, 'asc');
    }
  };

  // Get sorted leads
  const getSortedLeads = () => {
    const rawLeads = leads || [];
    const sortBy = filters.sortBy;
    const sortOrder = filters.sortOrder;
    if (!sortBy || rawLeads.length === 0) return rawLeads;

    return [...rawLeads].sort((a, b) => {
      let aValue, bValue;

      if (sortBy === 'leadId') {
        aValue = a.leadId || '';
        bValue = b.leadId || '';
      } else if (sortBy === 'leadDetails') {
        aValue = getLeadName(a).toLowerCase();
        bValue = getLeadName(b).toLowerCase();
      } else if (sortBy === 'source') {
        aValue = (a.source || '').toLowerCase();
        bValue = (b.source || '').toLowerCase();
      } else if (sortBy === 'score') {
        aValue = Number(a.score) || 0;
        bValue = Number(b.score) || 0;
      } else if (sortBy === 'status') {
        aValue = (a.status || '').toLowerCase();
        bValue = (b.status || '').toLowerCase();
      } else if (sortBy === 'owner') {
        aValue = (a.owner || '').toLowerCase();
        bValue = (b.owner || '').toLowerCase();
      } else if (sortBy === 'projectCost') {
        aValue = Number(a.totalCost) || 0;
        bValue = Number(b.totalCost) || 0;
      } else if (sortBy === 'leadGenerationDate') {
        aValue = new Date(a.leadGenerationDate || 0).getTime();
        bValue = new Date(b.leadGenerationDate || 0).getTime();
      } else if (sortBy === 'updatedAt') {
        aValue = new Date(a.updatedAt || a.createdAt || 0).getTime();
        bValue = new Date(b.updatedAt || b.createdAt || 0).getTime();
      } else if (sortBy === 'scoreLabel') {
        const order = { 'hot': 1, 'warm': 2, 'cold': 3 };
        aValue = order[(a.scoreLabel || 'cold').toLowerCase()] || 4;
        bValue = order[(b.scoreLabel || 'cold').toLowerCase()] || 4;
      } else if (sortBy === 'createdAt') {
        aValue = new Date(a.createdAt || 0).getTime();
        bValue = new Date(b.createdAt || 0).getTime();
      } else {
        aValue = a[sortBy] || '';
        bValue = b[sortBy] || '';
      }

      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Use leads directly from API - sorting is now handled on the frontend for immediate feedback
  const filteredLeads = useMemo(() => getSortedLeads(), [leads, filters.sortBy, filters.sortOrder]);

  const currentPageLeadIds = useMemo(
    () =>
      filteredLeads
        .map((lead) => {
          const raw = lead?._id ?? lead?.id ?? lead?.leadId;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [filteredLeads]
  );


  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState(null);

  // State for auto qualification
  const [isRunningAutoQualification, setIsRunningAutoQualification] = useState(false);
  const [autoQualificationProgress, setAutoQualificationProgress] = useState({ processed: 0, total: 0, qualified: 0 });
  const [showAutoQualificationModal, setShowAutoQualificationModal] = useState(false);

  // State for auto assignment
  const [isRunningAutoAssignment, setIsRunningAutoAssignment] = useState(false);
  const [showAutoAssignmentModal, setShowAutoAssignmentModal] = useState(false);

  // State for bulk delete
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);

  // State for bulk upload modal
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);

  // State for lead assignment dropdowns
  const [openDropdownLeadId, setOpenDropdownLeadId] = useState(null);
  const [leadSearchTerms, setLeadSearchTerms] = useState({});
  const [leadFilteredUsers, setLeadFilteredUsers] = useState({});
  const [leadAllUsers, setLeadAllUsers] = useState({});
  const [dropdownPositions, setDropdownPositions] = useState({});
  const leadDropdownRefs = useRef({});
  const [loadingUsers, setLoadingUsers] = useState(false);

  // State for bulk assign modal
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignee, setBulkAssignee] = useState(null);
  const [bulkAssignUsers, setBulkAssignUsers] = useState([]);
  const [bulkAssignSearchTerm, setBulkAssignSearchTerm] = useState('');

  const [isAssigningBulk, setIsAssigningBulk] = useState(false);

  // Conversion State
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedLeadForConversion, setSelectedLeadForConversion] = useState(null);

  // Track search focus

  // Ref to maintain focus on search input
  const searchInputRef = useRef(null);

  // Check for toast message from navigation state and display it
  useEffect(() => {
    if (location.state?.toast) {
      const { type, message } = location.state.toast;
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'warning' || type === 'error') {
        toast(message, { icon: '⚠️', style: { background: '#f59e0b', color: '#fff' } });
      }
      // Clear the state to prevent showing toast again on re-render
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  // Clear any stale errors when component mounts
  // This is a safety measure to prevent showing any lingering errors
  useEffect(() => {
    if (error) {
      // Clear error on mount - we're about to load fresh data anyway
      // The error will be cleared again when loadLeads succeeds
      clearError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount
  const fetchSources = async () => {
    try {
      const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_SOURCES}`);
      if (result.success) {
        const sources = result.data || [];
        setMasterSources(sources);
        masterSourcesRef.current = sources;
      }
    } catch (error) {
      console.error('Fetch sources error:', error);
    }
  };

  const fetchLeadDocumentTypes = async () => {
    try {
      const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_DOCUMENT_TYPES}`);
      if (result.success) {
        setMasterDocumentTypes(result.data || []);
      }
    } catch (error) {
      console.error('Fetch document types error:', error);
    }
  };

  const fetchIndustries = async () => {
    try {
      const result = await get(`${API_ENDPOINTS.CRM.CONFIG.INDUSTRIES}`);
      if (result.success) {
        setMasterIndustries(result.data || []);
      }
    } catch (error) {
      console.error('Fetch industries error:', error);
    }
  };

  const fetchDecisionMakers = async () => {
    try {
      const result = await get(`${API_ENDPOINTS.CRM.CONFIG.DECISION_MAKERS}`);
      if (result.success) {
        setMasterDecisionMakers(result.data || []);
      }
    } catch (error) {
      console.error('Fetch decision makers error:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_CATEGORIES}`);
      if (result.success) {
        setMasterCategories(result.data || []);
      }
    } catch (error) {
      console.error('Fetch categories error:', error);
    }
  };

  const fetchLeadStatuses = async () => {
    try {
      // Always get active statuses only
      const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_STATUSES}?active=true`);
      if (result.success) {
        setMasterStatuses(result.data || []);
      }
    } catch (error) {
      console.error('Fetch lead statuses error:', error);
    }
  };

  const fetchCompanySizes = async () => {
    try {
      const result = await get(`${API_ENDPOINTS.CRM.CONFIG.COMPANY_SIZES}`);
      if (result.success) setMasterCompanySizes(result.data || []);
    } catch (error) {
      console.error('Fetch company sizes error:', error);
    }
  };

  const fetchLeadBuckets = async () => {
    try {
      const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_BUCKETS}`);
      if (result.success) setMasterLeadBuckets(result.data || []);
    } catch (error) {
      console.error('Fetch lead buckets error:', error);
    }
  };

  const fetchBusinessUnits = async () => {
    try {
      const result = await get(`${API_ENDPOINTS.CRM.CONFIG.BUSINESS_UNITS}`);
      if (result.success) setMasterBusinessUnits(result.data || []);
    } catch (error) {
      console.error('Fetch business units error:', error);
    }
  };

  const fetchRevenueTypes = async () => {
    try {
      const result = await get(`${API_ENDPOINTS.CRM.CONFIG.REVENUE_TYPES}`);
      if (result.success) setMasterRevenueTypes(result.data || []);
    } catch (error) {
      console.error('Fetch revenue types error:', error);
    }
  };

  const fetchLastActiveChannels = async () => {
    try {
      const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LAST_ACTIVE_CHANNELS}`);
      if (result.success) setMasterLastActiveChannels(result.data || []);
    } catch (error) {
      console.error('Fetch last active channels error:', error);
    }
  };

  // Autofocus search input on mount
  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    fetchSources();
    fetchLeadDocumentTypes();
    fetchIndustries();
    fetchDecisionMakers();
    fetchCategories();
    fetchLeadStatuses();
    fetchCompanySizes();
    fetchLeadBuckets();
    fetchBusinessUnits();
    fetchRevenueTypes();
    fetchLastActiveChannels();
  }, []);

  // Load assignee options based on user role
  useEffect(() => {
    const loadAssignees = async () => {
      if (user?.role === USER_ROLES.SALES_EXECUTIVE) {
        setAssigneeOptions([]);
        return;
      }

      setLoadingAssignees(true);
      try {
        const params = new URLSearchParams();
        params.append('limit', 'all');

        if (user?.role === USER_ROLES.SUPER_ADMIN) {
          params.append('roleFilter', 'sales');
        } else if (user?.role === USER_ROLES.SALES_MANAGER) {
          params.append('roleFilter', 'sales_executive');
          params.append('managerId', user.id || user.userId || user._id);
        }

        const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        });

        const result = await response.json();
        if (result.responseCode === 200 && result.data?.users) {
          const crmUsers = result.data.users.map(u => ({
            id: u.user || u._id || u.id,
            name: u.name || 'Unknown',
            email: u.email || '',
            role: u.role || u.designation || ''
          }));
          // Add 'Unassigned' and 'Assigned Leads' options
          const options = [
            { id: 'Unassigned', name: 'Unassigned', email: '', role: '' },
            { id: 'Assigned', name: 'Assigned Leads', email: '', role: '' }
          ];

          if (user?.role === USER_ROLES.SALES_MANAGER && user) {
            const managerId = user.id || user.userId || user._id;
            const managerName = user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : (user.name || user.fullName || 'Me');
            if (!crmUsers.some(u => u.id === managerId)) {
              options.push({
                id: managerId,
                name: `${managerName} (Me)`,
                email: user.email || '',
                role: user.role || 'sales_manager'
              });
            }
          }

          options.push(...crmUsers);
          setAssigneeOptions(options);
          setFilteredAssigneeOptions(options);
        }
      } catch (error) {
        console.error('Failed to load assignees:', error);
        const defaultOptions = [
          { id: 'Unassigned', name: 'Unassigned', email: '', role: '' },
          { id: 'Assigned', name: 'Assigned Leads', email: '', role: '' }
        ];
        if (user?.role === USER_ROLES.SALES_MANAGER && user) {
          const managerId = user.id || user.userId || user._id;
          const managerName = user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : (user.name || user.fullName || 'Me');
          defaultOptions.push({
            id: managerId,
            name: `${managerName} (Me)`,
            email: user.email || '',
            role: user.role || 'sales_manager'
          });
        }
        setAssigneeOptions(defaultOptions);
        setFilteredAssigneeOptions(defaultOptions);
      } finally {
        setLoadingAssignees(false);
      }
    };

    loadAssignees();
  }, [user]);

  // Debounced search for assignee filter dropdown
  useEffect(() => {
    if (!openAssigneeDropdown) return;
    const handler = setTimeout(() => {
      if (assigneeSearchTerm.trim() === '') {
        setFilteredAssigneeOptions(assigneeOptions);
      } else {
        const searchLower = assigneeSearchTerm.toLowerCase();
        const filtered = assigneeOptions.filter(option =>
          option.name.toLowerCase().includes(searchLower) ||
          option.email.toLowerCase().includes(searchLower) ||
          option.role.toLowerCase().includes(searchLower)
        );
        setFilteredAssigneeOptions(filtered);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [assigneeSearchTerm, openAssigneeDropdown, assigneeOptions]);

  // Calculate assignee dropdown position
  useEffect(() => {
    if (openAssigneeDropdown && assigneeDropdownRef.current) {
      const rect = assigneeDropdownRef.current.getBoundingClientRect();
      setAssigneeDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [openAssigneeDropdown]);

  // Handle click outside assignee dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assigneeDropdownRef.current && !assigneeDropdownRef.current.contains(event.target)) {
        const dropdownElement = document.querySelector('[data-dropdown-assignee-filter]');
        if ((dropdownElement && !dropdownElement.contains(event.target)) || !dropdownElement) {
          setOpenAssigneeDropdown(false);
          setAssigneeSearchTerm('');
        }
      }
    };
    if (openAssigneeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openAssigneeDropdown]);

  // Helper function to get lead name
  const getLeadName = (lead) => {
    if (lead.firstName && lead.lastName) {
      return `${lead.firstName} ${lead.lastName}`;
    } else if (lead.firstName) {
      return lead.firstName;
    } else if (lead.lastName) {
      return lead.lastName;
    } else if (lead.name) {
      return lead.name;
    } else {
      return 'Unknown Lead';
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (limit) => {
    updateLimit(limit);
  };

  // Reset selection on filter/search change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [
    filters.search,
    filters.source,
    filters.status,
    filters.costType,
    filters.score,
    filters.owner,
    filters.assignee,
    filters.region,
    filters.startDate,
    filters.endDate,
    filters.limit
  ]);

  // Map backend source to frontend display (directly populate from backend resolved values)
  const mapSourceToFrontend = (source) => {
    if (!source) return 'Manual Entry';
    if (typeof source === 'object') {
      return source.name || source.label || source.id || 'Manual Entry';
    }
    return source;
  };

  // Map frontend source to backend source (directly map dynamically)
  const mapSourceToBackend = (source) => {
    if (!source) return 'manual_entry';

    // Check master sources first via ref to avoid stale closure
    const currentMasterSources = masterSourcesRef.current || [];
    const sourceLower = String(source).toLowerCase().trim();
    const masterSource = currentMasterSources.find(
      s => (s.name && s.name.toLowerCase() === sourceLower) || s._id === source || s.id === source
    );
    if (masterSource) return masterSource._id;

    return source.toLowerCase().replace(/\s+/g, '_');
  };

  // Map frontend status to backend stage
  const mapStatusToBackend = (status) => {
    const statusMap = {};
    // Populate map from master statuses
    masterStatuses.forEach(s => {
      const val = s.value || s.name;
      statusMap[s.name] = val;
      if (s.value) {
        statusMap[s.value] = val;
      }
    });

    return statusMap[status] || status?.toLowerCase() || 'new';
  };

  const mapStatusToFrontend = (status) => {
    if (!status) return '';
    const normalize = (str) => String(str || '').toLowerCase().replace(/[_\s-]/g, '');
    const normalizedInput = normalize(status);

    // Prioritize value matching first (e.g. backend stage 'contacted' -> name 'Suspect')
    let masterStatus = masterStatuses.find(s => s.value && normalize(s.value) === normalizedInput);
    if (!masterStatus) {
      masterStatus = masterStatuses.find(s => normalize(s.name) === normalizedInput);
    }

    if (masterStatus) {
      return masterStatus.name;
    }

    // Return the status formatted nicely if not found in master list (e.g. while loading)
    return String(status)
      .split(/[_\s]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const buildApiFilters = React.useCallback((overrides = {}) => {
    const apiFilters = {
      page: filters.page,
      limit: filters.limit,
      search: filters.search || undefined,
      source: filters.source !== 'all' ? mapSourceToBackend(filters.source) : undefined,
      stage: filters.status !== 'all' ? mapStatusToBackend(filters.status) : undefined,
      costType: filters.costType !== 'all' ? filters.costType : undefined,
      scoreLabel: filters.score !== 'all' ? filters.score.toLowerCase() : undefined,
      assignedTo: filters.assignee !== 'all' ? (filters.assignee === 'Unassigned' ? 'unassigned' : filters.assignee) : (filters.owner !== 'all' ? (filters.owner === 'Unassigned' ? 'unassigned' : filters.owner) : undefined),
      region: filters.region !== 'all' ? filters.region : undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      ...overrides
    };

    // Role-based filtering logic
    const isAdmin = user?.role === USER_ROLES.SUPER_ADMIN;
    const isManager = user?.role === USER_ROLES.SALES_MANAGER;

    if (isAdmin || isManager) {
      // Admin and Manager can see all leads, including unassigned
      if (filters.owner === 'all' && filters.assignee === 'all') {
        delete apiFilters.assignedTo;
      }
      delete apiFilters.region;
    } else if (user?.role === USER_ROLES.SALES_EXECUTIVE) {
      // Sales Executive should only see leads assigned to them
      // If no specific assignee/owner filter is active, enforce self-scope
      if (filters.owner === 'all' && filters.assignee === 'all') {
        apiFilters.assignedTo = user.id || user._id;
      }
    }

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [
    filters.page,
    filters.limit,
    filters.search,
    filters.source,
    filters.status,
    filters.costType,
    filters.score,
    filters.owner,
    filters.assignee,
    filters.region,
    filters.startDate,
    filters.endDate,
    masterSources,
    masterStatuses,
    user?.role,
    user?.id,
    user?._id
  ]);

  const baseExportFilters = React.useMemo(() => {
    const apiFilters = {
      search: filters.search || undefined,
      source: filters.source !== 'all' ? mapSourceToBackend(filters.source) : undefined,
      stage: filters.status !== 'all' ? mapStatusToBackend(filters.status) : undefined,
      costType: filters.costType !== 'all' ? filters.costType : undefined,
      scoreLabel: filters.score !== 'all' ? filters.score.toLowerCase() : undefined,
      assignedTo: filters.assignee !== 'all' ? (filters.assignee === 'Unassigned' ? 'unassigned' : filters.assignee) : (filters.owner !== 'all' ? (filters.owner === 'Unassigned' ? 'unassigned' : filters.owner) : undefined),
      region: filters.region !== 'all' ? filters.region : undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
    };

    // Role-based filtering logic
    const isAdmin = user?.role === USER_ROLES.SUPER_ADMIN;
    const isManager = user?.role === USER_ROLES.SALES_MANAGER;

    if (isAdmin || isManager) {
      // Admin and Manager can see all leads, including unassigned
      if (filters.owner === 'all' && filters.assignee === 'all') {
        delete apiFilters.assignedTo;
      }
      delete apiFilters.region;
    } else if (user?.role === USER_ROLES.SALES_EXECUTIVE) {
      // Sales Executive should only see leads assigned to them
      // If no specific assignee/owner filter is active, enforce self-scope
      if (filters.owner === 'all' && filters.assignee === 'all') {
        apiFilters.assignedTo = user.id || user._id;
      }
    }

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [
    filters.search,
    filters.source,
    filters.status,
    filters.costType,
    filters.score,
    filters.owner,
    filters.assignee,
    filters.region,
    filters.startDate,
    filters.endDate,
    masterSources,
    masterStatuses,
    user?.role,
    user?.id,
    user?._id
  ]);

  // Load leads from API when filters change - single effect to prevent duplicate calls
  useEffect(() => {
    // If we have active filters that require master data mapping, wait until master data is loaded
    const isWaitingForSources = filters.source !== 'all' && masterSources.length === 0;
    const isWaitingForStatuses = filters.status !== 'all' && masterStatuses.length === 0;

    if (isWaitingForSources || isWaitingForStatuses) {
      return;
    }

    loadLeads(buildApiFilters());
  }, [
    loadLeads,
    buildApiFilters,
    filters.source,
    filters.status,
    masterSources.length,
    masterStatuses.length
  ]);

  const getScoreColor = (score) => {
    switch (score) {
      case 'Hot': return 'bg-red-100 text-red-800';
      case 'Warm': return 'bg-yellow-100 text-yellow-800';
      case 'Cold': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    const backendValue = mapStatusToBackend(status);
    const normalizedStatus = String(backendValue || 'new').toLowerCase();

    // Try to find status in master list to see if it has a custom color (future enhancement)
    // For now, we use a standard mapping for common stages
    switch (normalizedStatus) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-green-100 text-green-800';
      case 'qualified': return 'bg-purple-100 text-purple-800';
      case 'converted': return 'bg-indigo-100 text-indigo-800';
      case 'disqualified': return 'bg-red-100 text-red-800';
      case 'closed_won': return 'bg-emerald-100 text-emerald-800';
      case 'closed_lost': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleViewLead = (leadId) => {
    if (onViewLead) {
      onViewLead(leadId);
    } else {
      navigate(`/lead_detail_360/${leadId}`);
    }
  };

  const handleEditLead = (leadId) => {
    // Navigate to manual lead entry page for editing
    if (onEditLead) {
      onEditLead(leadId);
    } else {
      navigate(`/manual_lead_entry?edit=${leadId}`, { state: { from: location.search } });
    }
  };

  const handleLogActivity = (leadId) => {
    if (onLogActivity) {
      onLogActivity(leadId);
    } else {
      navigate(`/log_activity/${leadId}`);
    }
  };

  const handleAssignLead = async (leadId, assigneeId) => {
    // If no assigneeId provided, perform default navigation behavior
    if (!assigneeId) {
      if (onAssignLead) {
        onAssignLead(leadId);
      } else {
        navigate('/lead_assignment');
      }
      return;
    }

    // Manual assignment logic
    try {
      const result = await assignmentRuleService.manuallyAssignLead(leadId, assigneeId, 'Manually assigned');
      if (result.success) {
        toast.success('Lead assigned successfully!');
        // Reload leads to reflect changes
        loadLeads(buildApiFilters());
      } else {
        toast.error(result.error || 'Failed to assign lead');
      }
    } catch (error) {

      toast.error('Failed to assign lead');
    }
  };

  const handleAddLead = () => {
    if (onAddLead) {
      onAddLead();
    } else {
      navigate('/manual_lead_entry', { state: { from: location.search } });
    }
  };

  // Load CRM users for assignee selection
  const loadUsers = React.useCallback(async (search = '', forLeadId = null) => {
    setLoadingUsers(true);
    try {
      const trimmedSearch = (search || '').trim();
      const params = new URLSearchParams();
      if (trimmedSearch) {
        params.append('search', trimmedSearch);
      }
      params.append('limit', forLeadId ? '20' : '50');
      params.append('roleFilter', 'sales');

      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        const crmUsers = result.data?.users || [];
        const transformedUsers = crmUsers.map(user => ({
          id: user.user || user._id || user.id,
          name: user.name || 'Unknown',
          email: user.email || '',
          department: user.department || '',
          role: user.role || '',
          designation: user.designation || ''
        })).sort((a, b) => a.name.localeCompare(b.name));

        if (forLeadId === 'bulk') {
          setBulkAssignUsers(transformedUsers);
          return transformedUsers;
        }

        if (forLeadId) {
          setLeadAllUsers(prev => ({ ...prev, [forLeadId]: transformedUsers }));
          setLeadFilteredUsers(prev => ({ ...prev, [forLeadId]: transformedUsers.slice(0, 10) }));
          return transformedUsers;
        }
        return [];
      }

      if (forLeadId) {
        setLeadFilteredUsers(prev => ({ ...prev, [forLeadId]: [] }));
        setLeadAllUsers(prev => ({ ...prev, [forLeadId]: [] }));
      }
      return [];
    } catch (error) {

      if (forLeadId) {
        setLeadFilteredUsers(prev => ({ ...prev, [forLeadId]: [] }));
        setLeadAllUsers(prev => ({ ...prev, [forLeadId]: [] }));
      }
      return [];
    } finally {
      setLoadingUsers(false);
    }

  }, []);

  // Simplified loadUsers for AssignmentRuleModal (similar to LeadAssignment logic)
  const loadUsersForRuleModal = React.useCallback(async (search = '') => {
    setLoadingUsers(true);
    try {
      const trimmedSearch = (search || '').trim();
      const params = new URLSearchParams();
      if (trimmedSearch) {
        params.append('search', trimmedSearch);
      }
      params.append('limit', '50');
      params.append('roleFilter', 'sales');

      const url = `${API_ENDPOINTS.CRM.TEAM_MANAGEMENT.USERS}${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const result = await response.json();

      if (result.responseCode === 200) {
        const crmUsers = result.data?.users || [];
        const transformedUsers = crmUsers.map(user => ({
          id: user.user || user._id || user.id,
          name: user.name || 'Unknown',
          email: user.email || '',
          department: user.department || '',
          role: user.role || '',
          designation: user.designation || ''
        })).sort((a, b) => a.name.localeCompare(b.name));

        setUsers(prev => {
          const existingMap = new Map(prev.map(u => [u.id, u]));
          transformedUsers.forEach(u => existingMap.set(u.id, u));
          return Array.from(existingMap.values());
        });
        setFilteredUsers(transformedUsers);
        return transformedUsers;
      }
      setFilteredUsers([]);
      return [];
    } catch (error) {

      setFilteredUsers([]);
      return [];
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadUsersForLead = React.useCallback(async (leadId, searchTerm = '') => {
    await loadUsers(searchTerm, leadId);
  }, [loadUsers]);

  // Debounced search for per-lead assignee dropdowns
  useEffect(() => {
    if (!openDropdownLeadId) return;
    const searchTerm = leadSearchTerms[openDropdownLeadId] || '';
    const handler = setTimeout(() => {
      loadUsersForLead(openDropdownLeadId, searchTerm);
    }, 400);
    return () => clearTimeout(handler);
  }, [openDropdownLeadId, leadSearchTerms, loadUsersForLead]);

  // Calculate dropdown position
  useEffect(() => {
    if (openDropdownLeadId) {
      const ref = leadDropdownRefs.current[openDropdownLeadId];
      if (ref) {
        const rect = ref.getBoundingClientRect();
        setDropdownPositions(prev => ({
          ...prev,
          [openDropdownLeadId]: {
            top: rect.bottom + window.scrollY + 8,
            left: rect.left + window.scrollX,
            width: rect.width
          }
        }));
      }
    }
  }, [openDropdownLeadId]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(leadDropdownRefs.current).forEach(leadId => {
        const ref = leadDropdownRefs.current[leadId];
        if (ref && !ref.contains(event.target)) {
          const dropdownElement = document.querySelector(`[data-dropdown-lead-id="${leadId}"]`);
          if ((dropdownElement && !dropdownElement.contains(event.target)) || (!dropdownElement && openDropdownLeadId === leadId)) {
            if (openDropdownLeadId === leadId) {
              setOpenDropdownLeadId(null);
              setLeadSearchTerms(prev => ({ ...prev, [leadId]: '' }));
            }
          }
        }
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownLeadId]);

  const mapLeadsForExport = (leadsToExport) => {
    return leadsToExport.map((lead, index) => ({

      'Lead generation Date': formatDate(lead.leadGenerationDate),
      'Client Name': lead.company || 'N/A',
      'Contact Person': getLeadName(lead),
      'Designation': lead.designation || 'N/A',
      'Whatsapp Phone': lead.whatsappPhone || 'N/A',
      'Email': lead.email || 'No email',
      'LinkedIn Profile URL': lead.linkedinProfileUrl || 'N/A',
      'Contact Details Raw': lead.contactDetailsRaw || 'N/A',
      'Company Name/Website': lead.company || 'N/A',
      'Lead Source': mapSourceToFrontend(lead.source),
      'Original Lead Generator': lead.createdBy?.fullName || lead.createdBy?.name || (lead.createdBy && typeof lead.createdBy === 'object' ? `${lead.createdBy.firstName || ''} ${lead.createdBy.lastName || ''}`.trim() : lead.createdBy) || 'N/A',
      'Current Lead Holder': lead.assignedTo?.fullName || lead.assignedTo?.name || (lead.assignedTo && typeof lead.assignedTo === 'object' ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() : lead.assignedTo) || lead.owner || 'Unassigned',
      'Business Unit': lead.businessUnit?.name || (typeof lead.businessUnit === 'object' ? '' : lead.businessUnit) || 'N/A',
      'Industry / Segment': lead.industry?.name || (typeof lead.industry === 'object' ? '' : lead.industry) || 'N/A',
      'Lead Bucket': lead.leadBucket?.name || (typeof lead.leadBucket === 'object' ? '' : lead.leadBucket) || 'N/A',
      'Current Stage': mapStatusToFrontend(lead.stage || lead.status),
      'Priority': lead.priority || 'N/A',
      'Probability': lead.probability || '0',
      'Current Remarks': lead.internalNotes || 'N/A',
      'Next Action': lead.nextAction || 'N/A',
      'Last Active Date': formatDate(lead.lastActiveDate),
      'Last Active Channel': lead.lastActiveChannel || 'N/A',
      'Last Followup Date': formatDate(lead.lastFollowupDate),
      'Next Follow-up Date': formatDate(lead.nextFollowupDate),
      'Follow-up Cadence': lead.followupCadence || 'N/A',
      'Follow-up Count': lead.followupCount || '0',
      'Requirements/Comments': lead.notes || lead.productRequirement || 'N/A',
      'Staging Instance': lead.stagingInstance || 'N/A',
      'Revenue Monthly': lead.revenueMonthly || '0',
      'Revenue One Time': lead.revenueOneTime || '0',
      'Revenue Type': lead.revenueType || 'N/A',
      'Expected Close Date / Comments': formatDate(lead.expectedCloseDate),
      'Loss Reason': lead.lossReason || 'N/A',
      'Need Management Intervention': lead.needManagementIntervention || 'N/A',
      'Lead ID': lead.leadId || 'N/A',
      'Score': lead.score || '0',
      'Project Cost': lead.totalCost ? formatIndianAmount(lead.totalCost) : '0',
      'Last Updated': formatDate(lead.updatedAt || lead.createdAt)
    }));
  };

  useEffect(() => {
    if (selectedRows.size === 0) {
      setShowInlineSelectedFormats(false);
    }
  }, [selectedRows.size]);

  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (
        selectedExportWrapRef.current &&
        !selectedExportWrapRef.current.contains(e.target)
      ) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageLeadIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'crm-leads',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? filters.page,
            limit: overrideLimit ?? filters.limit,
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `crm-leads-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      filters.page,
      filters.limit,
      filters.sortBy,
      filters.sortOrder,
      currentPageLeadIds,
    ]
  );

  const handleCrmLeadsSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected leads to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success(`${ids.length} lead(s) exported.`);
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  const handleCrmLeadsScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }

      setExportLoading(true);
      try {
        const totalFiltered = Number(pagination?.totalLeads ?? 0);

        if (scope === 'currentPage' && !filteredLeads.length) {
          toast.error('No data on current page to export.');
          return;
        }

        if (scope === 'filtered' && totalFiltered === 0) {
          toast.error('No leads match the current filters.');
          return;
        }

        if (scope === 'dateRange') {
          const startDate = meta?.startDate;
          const endDate = meta?.endDate;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: filters.limit,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageLeadIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      filteredLeads.length,
      pagination?.totalLeads,
      baseExportFilters,
      requestBackendExport,
      filters.limit,
      currentPageLeadIds,
    ]
  );

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredLeads.map(lead => lead.id || lead._id || lead.leadId);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleBulkAssignClick = () => {
    if (selectedRows.size > 0) {
      setShowBulkAssignModal(true);
      // Load users for the bulk assign dropdown
      loadUsers('', 'bulk');
    }
  };

  const handleConfirmBulkAssign = async () => {
    if (!bulkAssignee) {
      toast.error('Please select a user to assign leads to');
      return;
    }

    setIsAssigningBulk(true);
    try {
      const leadIds = Array.from(selectedRows);
      const result = await bulkAssignLeads(leadIds, bulkAssignee);

      if (result.success) {
        toast.success(result.message || `Successfully assigned ${leadIds.length} leads`);
        setSelectedRows(new Set());
        setShowBulkAssignModal(false);
        setBulkAssignee(null);
        // Refresh leads list
        await loadLeads(buildApiFilters());
      } else {
        toast.error(result.error || 'Failed to assign leads');
      }
    } catch (err) {

      toast.error(err.message || 'An error occurred during bulk assignment');
    } finally {
      setIsAssigningBulk(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.size > 0) {
      setShowBulkDeleteModal(true);
    }
  };

  const handleConfirmBulkDelete = async () => {
    setIsDeletingBulk(true);
    try {
      const leadIds = Array.from(selectedRows);
      const result = await bulkDeleteLeads(leadIds);

      if (result.success) {
        toast.success(result.message || `Successfully deleted ${leadIds.length} leads`);
        setSelectedRows(new Set());
        setShowBulkDeleteModal(false);
        // Refresh leads list
        await loadLeads(buildApiFilters());
      } else {
        toast.error(result.error || 'Failed to delete leads');
      }
    } catch (err) {

      toast.error(err.message || 'An error occurred during bulk deletion');
    } finally {
      setIsDeletingBulk(false);
    }
  };

  // --- Conversion Handlers ---

  const handleConvertLead = (lead) => {
    setSelectedLeadForConversion(lead);
    setShowConvertModal(true);
  };

  const handleConvertToOpportunity = async (lead, opportunityData) => {
    try {
      // Get lead ID (could be _id or leadId)
      const leadId = lead._id || lead.id || lead.leadId;

      if (!leadId) {
        throw new Error('Lead ID not found');
      }

      // Call API to convert lead to opportunity
      const result = await leadService.convertToOpportunity(leadId, opportunityData);

      if (result.success) {
        const leadName = getLeadName(lead);
        toast.success(`Lead "${leadName}" converted to opportunity successfully!`);

        // Close modal
        setSelectedLeadForConversion(null);
        setShowConvertModal(false);

        // Refresh leads list
        await loadLeads(buildApiFilters());
      } else {
        throw new Error(result.error || 'Failed to convert lead to opportunity');
      }
    } catch (error) {

      let errorMessage = 'Failed to convert lead to opportunity';
      if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      throw error; // Re-throw to let the modal know it failed
    }
  };

  const getLeadCurrentStatusValue = (lead) => {
    const stageNormalized = String(lead.stage || '').toLowerCase().trim();
    const statusNormalized = String(lead.status || '').toLowerCase().trim();

    const matchByStage = masterStatuses.find(s => s.value && s.value.toLowerCase().trim() === stageNormalized);
    if (matchByStage) return matchByStage.value || matchByStage.name;

    const matchByName = masterStatuses.find(s => s.name && s.name.toLowerCase().trim() === statusNormalized);
    if (matchByName) return matchByName.value || matchByName.name;

    const matchByStageName = masterStatuses.find(s => s.name && s.name.toLowerCase().trim() === stageNormalized);
    if (matchByStageName) return matchByStageName.value || matchByStageName.name;

    return lead.stage || 'new';
  };

  const handleStatusChange = async (leadId, newStatus) => {
    const statusToastId = toast.loading('Updating status...');
    try {
      const response = await leadService.updateLead(leadId, { status: newStatus });
      if (response && response.success) {
        toast.success(response.message || 'Status updated successfully', { id: statusToastId });
        await loadLeads(buildApiFilters());
      } else {
        toast.error(response.error || 'Failed to update status', { id: statusToastId });
      }
    } catch (err) {
      toast.error(err.message || 'An error occurred while updating status', { id: statusToastId });
    }
  };

  // Show delete confirmation modal - DO NOT call API here
  const handleDeleteClick = (leadId) => {
    setLeadToDelete(leadId);
    setShowDeleteModal(true);
  };

  // Close delete modal without deleting
  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setLeadToDelete(null);
  };

  // Confirm delete - ONLY call API when user clicks Confirm button
  const handleConfirmDelete = async () => {
    if (!leadToDelete) {
      setShowDeleteModal(false);
      return;
    }

    try {
      const result = await deleteLead(leadToDelete);
      if (result.success) {
        toast.success('Lead deleted successfully');
        // Refresh the leads list with current pagination
        await loadLeads(buildApiFilters());
      } else {
        toast(result.error || 'Unable to delete lead. Please try again.', {
          icon: '⚠️',
          style: { background: '#f59e0b', color: '#fff' }
        });
      }
    } catch (error) {

      toast('Unable to delete lead. Please check your connection and try again.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
    } finally {
      // Close modal after API call completes
      setShowDeleteModal(false);
      setLeadToDelete(null);
    }
  };

  // Show confirmation modal for auto qualification
  const handleShowAutoQualificationModal = () => {
    setShowAutoQualificationModal(true);
  };

  // Run auto qualification for all unqualified leads (called after confirmation)
  const handleRunAutoQualification = async () => {
    setShowAutoQualificationModal(false);
    setIsRunningAutoQualification(true);
    setAutoQualificationProgress({ processed: 0, total: 0, qualified: 0 });

    try {
      // First, get all unqualified leads (we'll fetch in batches)
      let allUnqualifiedLeads = [];
      let currentPage = 1;
      let hasMore = true;
      const batchSize = 100; // Process 100 leads at a time

      // Fetch all unqualified leads
      while (hasMore) {
        const result = await leadScoringService.getLeadScores({
          page: currentPage,
          limit: batchSize,
          isQualified: false
        });

        if (result.success && result.leads && result.leads.length > 0) {
          allUnqualifiedLeads = [...allUnqualifiedLeads, ...result.leads];
          currentPage++;
          hasMore = result.pagination?.hasNextPage || false;
        } else {
          hasMore = false;
        }
      }

      const totalLeads = allUnqualifiedLeads.length;
      setAutoQualificationProgress({ processed: 0, total: totalLeads, qualified: 0 });

      if (totalLeads === 0) {
        toast('No disqualified leads found to process', {
          icon: 'ℹ️',
          style: { background: '#3b82f6', color: '#fff' }
        });
        setIsRunningAutoQualification(false);
        return;
      }

      let qualifiedCount = 0;
      let processedCount = 0;

      // Process leads in smaller batches to avoid overwhelming the server
      const processBatchSize = 10;
      for (let i = 0; i < allUnqualifiedLeads.length; i += processBatchSize) {
        const batch = allUnqualifiedLeads.slice(i, i + processBatchSize);

        // Process batch in parallel - only qualify, don't recalculate scores
        const batchResults = await Promise.all(
          batch.map(async (lead) => {
            try {
              const leadId = lead._id || lead.id || lead.leadId;
              const result = await leadScoringService.qualifyLeadOnly(leadId);

              return {
                success: result.success,
                isQualified: result.data?.isQualified || false
              };
            } catch (error) {

              return {
                success: false,
                isQualified: false
              };
            }
          })
        );

        // Update counts after batch completes
        batchResults.forEach((result) => {
          processedCount++;
          if (result.success && result.isQualified) {
            qualifiedCount++;
          }
        });

        // Update progress
        setAutoQualificationProgress({
          processed: processedCount,
          total: totalLeads,
          qualified: qualifiedCount
        });

        // Small delay between batches to avoid overwhelming the server
        if (i + processBatchSize < allUnqualifiedLeads.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      // Reload leads to show updated results
      await loadLeads(buildApiFilters());

      toast.success(
        `Processed ${processedCount} leads, ${qualifiedCount} newly qualified.`,
        { duration: 6000 }
      );
    } catch (error) {

      toast('Failed to run auto-qualification. Please try again.', {
        icon: '⚠️',
        style: { background: '#f59e0b', color: '#fff' }
      });
    } finally {
      setIsRunningAutoQualification(false);
      setAutoQualificationProgress({ processed: 0, total: 0, qualified: 0 });
    }
  };

  // Show confirmation modal for auto assignment
  const handleShowAutoAssignmentModal = () => {
    setShowAutoAssignmentModal(true);
  };

  // Run auto assignment
  const handleRunAutoAssignment = async () => {
    setShowAutoAssignmentModal(false);
    setIsRunningAutoAssignment(true);
    try {
      const result = await assignmentRuleService.runAutoAssignment();
      if (result.success) {
        toast.success(
          `Auto-assignment completed. Processed ${result.results.processedCount || 0} leads, assigned ${result.results.assignedCount || 0}.`,
          { duration: 5000 }
        );
        // Refresh leads
        await loadLeads(buildApiFilters());
      } else {
        toast.error(result.error || 'Failed to run auto-assignment');
      }
    } catch (error) {

      toast.error('Failed to run auto-assignment');
    } finally {
      setIsRunningAutoAssignment(false);
    }
  };

  // Check if any filters or search are active
  const hasActiveFilters = useMemo(() => {
    return (
      searchTerm.trim() !== '' ||
      filters.source !== 'all' ||
      filters.status !== 'all' ||
      filters.costType !== 'all' ||
      filters.score !== 'all' ||
      filters.owner !== 'all' ||
      filters.assignee !== 'all' ||
      filters.region !== 'all' ||
      filters.startDate !== '' ||
      filters.endDate !== ''
    );
  }, [
    searchTerm,
    filters.source,
    filters.status,
    filters.costType,
    filters.score,
    filters.owner,
    filters.assignee,
    filters.region,
    filters.startDate,
    filters.endDate
  ]);

  // Clear all filters and search - let useEffect handle the API call (same as LeadActivities)
  const handleClearFilters = () => {
    resetFilters();
  };


  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="p-3 bg-red-100 rounded-full mb-4 mx-auto w-fit">
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Leads</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            className="btn-create"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center gap-4">
          <div className="bg-slate-900 p-2.5 rounded-xl shadow-sm border border-slate-800 flex items-center justify-center">
            <Users className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight">Lead List & Filters</h1>
            <p className="text-sm sm:text-base text-slate-500 mt-1">Manage and filter your leads with advanced search capabilities</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <LeadManagementGate action="create">
            <button
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              onClick={handleAddLead}
            >
              <Plus className="h-4 w-4" />
              <span>Add Lead</span>
            </button>
          </LeadManagementGate>
          {canModify && (
            <>
              <button
                className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
                onClick={handleShowAutoQualificationModal}
                disabled={isRunningAutoQualification}
              >
                {isRunningAutoQualification ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Target className="h-4 w-4" />}
                <span>
                  {isRunningAutoQualification
                    ? `Processing... (${autoQualificationProgress.processed}/${autoQualificationProgress.total})`
                    : 'Run Auto Qualification'
                  }
                </span>
              </button>
              <button
                className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
                onClick={handleShowAutoAssignmentModal}
                disabled={isRunningAutoAssignment}
              >
                {isRunningAutoAssignment ? <RefreshCw className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                <span>{isRunningAutoAssignment ? 'Running...' : 'Run Auto Assignment'}</span>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-3 flex-wrap gap-y-2">
        {user?.role !== USER_ROLES.SALES_EXECUTIVE && (
          <div className="relative">
            <button
              ref={assigneeDropdownRef}
              onClick={() => {
                const isOpening = !openAssigneeDropdown;
                setOpenAssigneeDropdown(isOpening);
                if (isOpening) {
                  setFilteredAssigneeOptions(assigneeOptions);
                }
              }}
              disabled={loadingAssignees}
              className="px-3 py-2 border border-gray-300 rounded-lg flex items-center justify-between text-left bg-white focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed h-[42px]"
            >
              <span className="truncate text-sm whitespace-nowrap min-w-[120px]">
                {filters.assignee === 'all' ? 'All Assignees' : assigneeOptions.find(opt => opt.id === filters.assignee)?.name || 'All Assignees'}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openAssigneeDropdown ? 'rotate-180' : ''}`} />
            </button>

            {/* Assignee Dropdown */}
            {openAssigneeDropdown && (
              <div
                data-dropdown-assignee-filter
                className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                style={{
                  top: `${assigneeDropdownPosition.top}px`,
                  left: `${assigneeDropdownPosition.left}px`,
                  minWidth: '240px',
                  maxWidth: '350px',
                  width: `${Math.max(240, assigneeDropdownPosition.width)}px`
                }}
              >
                <div className="p-2 border-b border-gray-100">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={assigneeSearchTerm}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                        setAssigneeSearchTerm(value);
                      }}
                      placeholder="Search assignees..."
                      maxLength={100}
                      className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#0F2942]/50 focus:border-transparent"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                  {loadingAssignees && filteredAssigneeOptions.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">
                      Loading assignees...
                    </div>
                  ) : filteredAssigneeOptions.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500 text-center">
                      {assigneeSearchTerm ? 'No assignees found' : 'No assignees available'}
                    </div>
                  ) : (
                    <div className="flex flex-col">
                      <button
                        type="button"
                        onClick={() => {
                          updateFilter({ assignee: 'all' });
                          setOpenAssigneeDropdown(false);
                          setAssigneeSearchTerm('');
                        }}
                        className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 flex flex-col ${filters.assignee === 'all'
                          ? 'bg-blue-50 text-[#0F2942] font-medium'
                          : 'hover:bg-gray-50 text-gray-700'
                          }`}
                      >
                        <div className="font-medium">All Assignees</div>
                      </button>
                      {filteredAssigneeOptions.map(option => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            updateFilter({ assignee: option.id });
                            setOpenAssigneeDropdown(false);
                            setAssigneeSearchTerm('');
                          }}
                          className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 flex flex-col ${filters.assignee === option.id
                            ? 'bg-blue-50 text-[#0F2942] font-medium'
                            : 'hover:bg-gray-50 text-gray-700'
                            }`}
                        >
                          <div className="truncate font-medium break-words">{option.name}</div>
                          {option.email && (
                            <div className="text-xs text-gray-500 truncate break-words">{option.email}</div>
                          )}
                          {option.role && (
                            <div className="text-xs text-gray-500 truncate capitalize break-words">{option.role}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        <LeadManagementGate action="read">
          <div className="relative shrink-0" ref={selectedExportWrapRef}>
            <Button
              variant="outline"
              icon={
                exportLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-[#0F2942] border-t-transparent" />
                ) : (
                  <Download className="h-4 w-4" />
                )
              }
              onClick={handleExportClick}
              disabled={exportLoading}
              className={`flex-shrink-0 ${selectedRows.size > 0 ? 'bg-blue-50 border-blue-200 text-[#0F2942]' : ''}`}
              aria-label={
                selectedRows.size > 0
                  ? `Export ${selectedRows.size} selected leads`
                  : 'Export leads'
              }
            >
              {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
            </Button>
            <SelectedExportFormatBar
              show={selectedRows.size > 0 && showInlineSelectedFormats}
              panelEntered={inlineFormatPanelEntered}
              disabled={exportLoading}
              formats={['csv', 'xlsx', 'pdf']}
              onSelect={handleCrmLeadsSelectedFormatExport}
            />
          </div>
        </LeadManagementGate>
        {selectedRows.size > 0 && (
          <LeadManagementGate action="update">
            <button
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              onClick={handleBulkAssignClick}
            >
              <UserPlus className="h-4 w-4" />
              <span>Assign ({selectedRows.size})</span>
            </button>
          </LeadManagementGate>
        )}
        {selectedRows.size > 0 && (
          <LeadManagementGate action="delete">
            <button
              className="flex items-center justify-center space-x-1.5 !bg-red-600 hover:!bg-red-700 !text-white rounded-md px-3 py-1.5 text-[13px] font-medium h-8 shadow-sm transition-colors"
              onClick={handleBulkDelete}
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete ({selectedRows.size})</span>
            </button>
          </LeadManagementGate>
        )}
        <LeadManagementGate action="create">
          <button
            className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
            onClick={async () => {
              // Fetch extra masters only when bulk upload is clicked
              try {
                // Run in parallel for speed
                await Promise.all([
                  fetchSources(),
                  fetchLeadStatuses(),
                  fetchLeadDocumentTypes(),
                  fetchIndustries(),
                  fetchDecisionMakers(),
                  fetchCategories(),
                  fetchCompanySizes(),
                  fetchLeadBuckets(),
                  fetchBusinessUnits(),
                  fetchRevenueTypes(),
                  fetchLastActiveChannels()
                ]);
              } catch (err) {
                console.error('Failed to load upload instructions:', err);
              }
              setShowBulkUploadModal(true);
            }}
          >
            <Upload className="h-4 w-4" />
            <span>Bulk Upload</span>
          </button>
        </LeadManagementGate>
        {canModify && (
          <>
            <button
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              onClick={() => {
                setShowRuleModal(true);
                loadUsersForRuleModal(''); // Initial load of users
              }}
            >
              <Plus className="h-4 w-4" />
              <span>Assignment Rule</span>
            </button>
            <button
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              onClick={() => {
                setSelectedScoring(null); // Create mode
                setScoringFormData({
                  name: '',
                  description: '',
                  rules: [],
                  isActive: true
                });
                setScoringErrors({});
                setShowScoringModal(true);
              }}
            >
              <Plus className="h-4 w-4" />
              <span>Scoring Rule</span>
            </button>
            <button
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm transition-colors rounded-md px-3 py-1.5 text-[13px] font-medium flex items-center justify-center space-x-1.5 h-8"
              onClick={() => {
                setSelectedCriteria(null);
                setCriteriaFormData({
                  name: '',
                  description: '',
                  criteria: [],
                  isActive: true,
                  qualificationLogic: 'all',
                  qualificationResult: 'qualified',
                  leadTemperature: 'Cold'
                });
                setCriteriaErrors({});
                setShowCriteriaModal(true);
              }}
            >
              <Plus className="h-4 w-4" />
              <span>Qualification Criteria</span>
            </button>
          </>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col xl:flex-row gap-3 xl:items-center justify-between">
          <div className="relative flex-1 min-w-[240px] sm:min-w-[280px] w-full xl:w-auto">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                const sanitized = handleSearchInputChange(e, null, FIELD_LIMITS.search || 200);
                setSearchTerm(sanitized);
              }}
              placeholder="Search by name, company, email, phone, Lead ID..."
              className="input-field pl-10 pr-4 w-full"
              maxLength={FIELD_LIMITS.search || 200}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto">
            <select
              value={filters.source}
              onChange={(e) => updateFilter({ source: e.target.value })}
              className="input-field w-full sm:w-auto min-w-[120px]"
            >
              <option value="all">All Sources</option>
              {masterSources.map(source => (
                <option key={source._id} value={source.name} className="capitalize">{source.name}</option>
              ))}

            </select>
            <select
              value={filters.status}
              onChange={(e) => updateFilter({ status: e.target.value })}
              className="input-field w-full sm:w-auto min-w-[120px]"
            >
              <option value="all">All Status</option>
              {masterStatuses.map(status => (
                <option key={status._id} value={status.value || status.name}>{status.name}</option>
              ))}
            </select>

            <select
              value={filters.score}
              onChange={(e) => updateFilter({ score: e.target.value })}
              className="input-field w-full sm:w-auto min-w-[110px]"
            >
              <option value="all">All Temp</option>
              <option value="Hot">Hot</option>
              <option value="Warm">Warm</option>
              <option value="Cold">Cold</option>
            </select>

            <select
              value={filters.costType}
              onChange={(e) => updateFilter({ costType: e.target.value })}
              className="input-field w-full sm:w-auto min-w-[130px]"
            >
              <option value="all">All Cost Types</option>
              <option value="fixed">Fixed Cost</option>
              <option value="recurring">Recurring Cost</option>
            </select>

            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={filters.startDate}
                endDate={filters.endDate}
                onChange={({ startDate, endDate }) => updateFilter({ startDate, endDate })}
              />
            </div>
            {hasActiveFilters && (
              <button
                className="btn-cancel flex items-center justify-center space-x-2 flex-shrink-0"
                onClick={handleClearFilters}
              >
                <X className="h-4 w-4" />
                <span>Clear</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Loading State - Only show in table area, not full page */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading leads...</p>
            </div>
          </div>
        )}

        {/* Desktop Table View */}
        {!loading && (
          <div className="hidden lg:block overflow-x-auto w-full">
            <table className="min-w-[1200px] divide-y divide-gray-200 table-auto w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={filteredLeads.length > 0 && selectedRows.size === filteredLeads.length}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded cursor-pointer"
                    />
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('leadId')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Lead ID</span>
                      {sortColumn === 'leadId' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]">
                    Actions
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[150px]">
                    Change Status
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[220px] max-w-[220px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('leadDetails')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Lead Details</span>
                      {sortColumn === 'leadDetails' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('source')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Source</span>
                      {sortColumn === 'source' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('score')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Score</span>
                      {sortColumn === 'score' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortColumn === 'status' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('scoreLabel')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Temperature</span>
                      {sortColumn === 'scoreLabel' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('owner')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Owner</span>
                      {sortColumn === 'owner' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[130px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('projectCost')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Project Cost</span>
                      {sortColumn === 'projectCost' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('leadGenerationDate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Generation Date</span>
                      {sortColumn === 'leadGenerationDate' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created At</span>
                      {sortColumn === 'createdAt' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[140px] cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('updatedAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Last Updated</span>
                      {sortColumn === 'updatedAt' ? (
                        sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                      ) : (
                        <ArrowUpDown className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </th>
                  {user?.role !== USER_ROLES.SALES_EXECUTIVE && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                      Assignee
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan="16" className="px-6 py-12 text-center">
                      <div className="text-gray-500">
                        <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                        <p className="text-lg font-medium">No leads found</p>
                        <p className="text-sm mt-2">
                          {leads.length === 0
                            ? 'No leads have been created yet. Create your first lead to get started.'
                            : 'Try adjusting your filters to see more results.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead) => {
                    const leadId = lead.id || lead._id || lead.leadId;
                    return (
                      <tr key={leadId} className={`hover:bg-gray-50 ${selectedRows.has(leadId) ? 'bg-green-50' : ''}`}>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(leadId)}
                            onChange={() => handleSelectRow(leadId)}
                            className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded cursor-pointer"
                          />
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <span
                            className="text-xs text-center text-gray-900 font-medium font-mono cursor-pointer hover:text-[#0F2942] hover:underline transition-colors"
                            title={lead.leadId || 'No ID'}
                            onClick={() => handleViewLead(leadId)}
                          >
                            {lead.leadId || 'No ID'}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium min-w-[100px]">
                          <div className="flex items-center space-x-2">
                            <LeadManagementGate action="read">
                              <button
                                onClick={() => handleViewLead(leadId)}
                                className="text-[#0F2942] hover:text-blue-900 flex-shrink-0"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </LeadManagementGate>
                            <LeadManagementGate action="update">
                              <button
                                onClick={() => handleEditLead(leadId)}
                                className="text-green-600 hover:text-green-900 flex-shrink-0"
                                title="Edit Lead"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </LeadManagementGate>
                            <LeadManagementGate action="create">
                              <button
                                onClick={() => handleLogActivity(leadId)}
                                className="text-amber-600 hover:text-amber-900 flex-shrink-0"
                                title="Log Activity"
                              >
                                <ClipboardList className="h-4 w-4" />
                              </button>
                            </LeadManagementGate>
                            <LeadManagementGate action="create">
                              <button
                                onClick={() => handleConvertLead(lead)}
                                disabled={lead.status === 'Converted'}
                                className={`flex-shrink-0 ${lead.status === 'Converted'
                                  ? 'text-gray-400 cursor-not-allowed'
                                  : 'text-indigo-600 hover:text-indigo-900'
                                  }`}
                                title={lead.status === 'Converted' ? 'Already Converted' : 'Convert to Opportunity'}
                              >
                                <ArrowRight className="h-4 w-4" />
                              </button>
                            </LeadManagementGate>
                            <LeadManagementGate action="delete">
                              <button
                                onClick={() => handleDeleteClick(leadId)}
                                className="flex-shrink-0 text-red-600 hover:text-red-900"
                                title="Delete Lead"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </LeadManagementGate>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium min-w-[150px]">
                          <select
                            value={getLeadCurrentStatusValue(lead)}
                            onChange={(e) => handleStatusChange(leadId, e.target.value)}
                            className="input-field py-1 px-2 text-xs border border-gray-300 rounded focus:ring-brand focus:border-brand w-full max-w-[140px] bg-white cursor-pointer"
                          >
                            {masterStatuses.map(status => (
                              <option key={status._id} value={status.value || status.name}>
                                {status.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-4 min-w-[200px] max-w-[200px]">
                          <div className="flex items-center space-x-3 min-w-0">
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <div
                                className="text-sm font-medium text-gray-900 truncate capitalize cursor-pointer hover:text-[#0F2942] hover:underline transition-colors"
                                title={getLeadName(lead)}
                                onClick={() => handleViewLead(leadId)}
                              >
                                {getLeadName(lead)}
                              </div>

                              <div className="text-sm text-gray-500 truncate first-letter:capitalize" title={lead.company || 'No company'}>{lead.company || 'No company'}</div>
                              <div className="text-xs text-gray-400 truncate" title={lead.email || 'No email'}>{lead.email || 'No email'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <span className="text-sm text-gray-900 truncate block capitalize" title={mapSourceToFrontend(lead.source)}>{mapSourceToFrontend(lead.source)}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 min-w-[80px]">
                          {lead.score}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[100px]">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(lead.status || 'New')}`}>
                            {mapStatusToFrontend(lead.status)}
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[120px]">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${lead.scoreLabel === 'hot' ? 'bg-red-100 text-red-800' :
                            lead.scoreLabel === 'warm' ? 'bg-orange-100 text-orange-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                            {lead.scoreLabel === 'hot' ? '🔥 Hot' :
                              lead.scoreLabel === 'warm' ? '☀️ Warm' :
                                '❄️ Cold'
                            }
                          </span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[200px] max-w-[220px]">
                          <div className="overflow-hidden">
                            <span className="text-sm text-gray-900 first-letter:capitalize truncate block" title={lead.owner || 'Unassigned'}>{lead.owner || 'Unassigned'}</span>
                            {lead.ownerEmail && (
                              <span className="text-xs text-gray-500 truncate block" title={lead.ownerEmail}>{lead.ownerEmail}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[130px]">
                          <span className="text-sm font-medium text-gray-900">₹{lead.totalCost ? formatIndianAmount(lead.totalCost) : '0'}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-500">{formatDate(lead.leadGenerationDate)}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-500">{formatDate(lead.createdAt)}</span>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="text-sm text-gray-500">{formatDate(lead.updatedAt || lead.createdAt)}</span>
                        </td>
                        {user?.role !== USER_ROLES.SALES_EXECUTIVE && (
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium" style={{ position: 'relative', overflow: 'visible' }}>
                            <div className="relative w-full" ref={el => leadDropdownRefs.current[leadId] = el}>
                              <button
                                type="button"
                                onClick={() => {
                                  const isOpening = openDropdownLeadId !== leadId;
                                  setOpenDropdownLeadId(isOpening ? leadId : null);
                                  if (isOpening && !leadAllUsers[leadId]) {
                                    loadUsersForLead(leadId, leadSearchTerms[leadId] || '');
                                  }
                                }}
                                disabled={loadingUsers}
                                className="input-field px-3 py-1.5 flex items-center justify-between text-left bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                              >
                                <span className="truncate text-sm">Select Assignee</span>
                                <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${openDropdownLeadId === leadId ? 'rotate-180' : ''}`} />
                              </button>

                              {/* Dropdown */}
                              {openDropdownLeadId === leadId && dropdownPositions[leadId] && (
                                <div
                                  data-dropdown-lead-id={leadId}
                                  className="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl"
                                  style={{
                                    top: `${dropdownPositions[leadId].top}px`,
                                    left: `${dropdownPositions[leadId].left}px`,
                                    minWidth: '200px',
                                    maxWidth: '300px',
                                    width: `${Math.max(200, dropdownPositions[leadId].width)}px`
                                  }}
                                >
                                  <div className="p-2 border-b border-gray-100">
                                    <div className="relative">
                                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                      <input
                                        type="text"
                                        value={leadSearchTerms[leadId] || ''}
                                        onChange={(e) => {
                                          const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                                          setLeadSearchTerms(prev => ({
                                            ...prev,
                                            [leadId]: value
                                          }));
                                        }}
                                        placeholder="Search users..."
                                        maxLength={100}
                                        className="input-field text-sm"
                                        autoFocus
                                      />
                                    </div>
                                  </div>
                                  <div className="overflow-y-auto max-h-[240px] overflow-x-hidden">
                                    {loadingUsers && (!leadFilteredUsers[leadId] || leadFilteredUsers[leadId].length === 0) ? (
                                      <div className="p-3 text-sm text-gray-500 text-center">
                                        Loading users...
                                      </div>
                                    ) : (!leadFilteredUsers[leadId] || leadFilteredUsers[leadId].length === 0) ? (
                                      <div className="p-3 text-sm text-gray-500 text-center">
                                        {leadSearchTerms[leadId] ? 'No users found' : 'No users available'}
                                      </div>
                                    ) : (
                                      <div className="flex flex-col">
                                        {leadFilteredUsers[leadId].map(user => (
                                          <button
                                            key={user.id}
                                            type="button"
                                            onClick={() => {
                                              handleAssignLead(leadId, user.id);
                                              setOpenDropdownLeadId(null);
                                              setLeadSearchTerms(prev => ({
                                                ...prev,
                                                [leadId]: ''
                                              }));
                                            }}
                                            className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-gray-700 flex flex-col"
                                          >
                                            <div className="truncate font-medium break-words">{user.name}</div>
                                            {user.email && (
                                              <div className="text-xs text-gray-500 truncate break-words">{user.email}</div>
                                            )}
                                            {user.designation && (
                                              <div className="text-xs text-gray-500 truncate capitalize break-words">{user.designation}</div>
                                            )}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Mobile Card View */}
        {!loading && (
          <div className="lg:hidden">
            {filteredLeads.length > 0 && (
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedRows.size === filteredLeads.length}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded cursor-pointer"
                />
                <span className="text-sm font-medium text-gray-700">Select All ({filteredLeads.length})</span>
              </div>
            )}
            {filteredLeads.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium text-gray-900 mb-2">No leads found</p>
                <p className="text-sm text-gray-500">
                  {leads.length === 0
                    ? 'No leads have been created yet.'
                    : 'Try adjusting your filters.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredLeads.map((lead) => {
                  const leadId = lead.id || lead._id || lead.leadId;
                  return (
                    <div key={leadId} className={`p-4 hover:bg-gray-50 ${selectedRows.has(leadId) ? 'bg-green-50' : ''}`}>
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-1">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(leadId)}
                            onChange={() => handleSelectRow(leadId)}
                            className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded cursor-pointer"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-2">
                            <h4
                              className="text-sm font-medium text-gray-900 truncate capitalize cursor-pointer hover:text-[#0F2942] hover:underline transition-colors"
                              onClick={() => handleViewLead(leadId)}
                            >
                              {getLeadName(lead)}
                            </h4>
                            <span className="text-xs font-medium text-gray-900">
                              {lead.score}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 truncate first-letter:capitalize">{lead.company || 'No company'}</p>
                          <p className="text-xs text-gray-400 truncate">{lead.email || 'No email'}</p>

                          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mt-3">
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">Source:</span>
                              <span className="text-xs font-medium text-gray-900 truncate" title={mapSourceToFrontend(lead.source)}>{mapSourceToFrontend(lead.source)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">Status:</span>
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusColor(lead.status || 'New')}`}>
                                {mapStatusToFrontend(lead.status)}
                              </span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500">Temp:</span>
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold capitalize ${lead.scoreLabel === 'hot' ? 'bg-red-100 text-red-800' :
                                lead.scoreLabel === 'warm' ? 'bg-orange-100 text-orange-800' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                {lead.scoreLabel === 'hot' ? '🔥 Hot' :
                                  lead.scoreLabel === 'warm' ? '☀️ Warm' :
                                    '❄️ Cold'
                                }
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center justify-between mt-3">
                            <div className="text-xs text-gray-500">
                              <span className="block">Owner: <span className="first-letter:capitalize">{lead.owner || 'Unassigned'}</span></span>
                              <span className="block">Project Cost: ₹{lead.totalCost ? formatIndianAmount(lead.totalCost) : '0'}</span>
                              <span className="block">Created: {formatDate(lead.createdAt)}</span>
                              <span className="block">Last Activity: {formatDate(lead.updatedAt || lead.createdAt)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <LeadManagementGate action="read">
                                <button
                                  onClick={() => handleViewLead(leadId)}
                                  className="text-[#0F2942] hover:text-blue-900"
                                  title="View Details"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                              </LeadManagementGate>
                              <LeadManagementGate action="update">
                                <button
                                  onClick={() => handleEditLead(leadId)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Edit Lead"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                              </LeadManagementGate>
                              <LeadManagementGate action="create">
                                <button
                                  onClick={() => handleLogActivity(leadId)}
                                  className="text-amber-600 hover:text-amber-900"
                                  title="Log Activity"
                                >
                                  <ClipboardList className="h-4 w-4" />
                                </button>
                              </LeadManagementGate>
                              <LeadManagementGate action="create">
                                <button
                                  onClick={() => handleConvertLead(lead)}
                                  disabled={lead.status === 'Converted'}
                                  className={lead.status === 'Converted'
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-indigo-600 hover:text-indigo-900'
                                  }
                                  title={lead.status === 'Converted' ? 'Already Converted' : 'Convert to Opportunity'}
                                >
                                  <ArrowRight className="h-4 w-4" />
                                </button>
                              </LeadManagementGate>
                              <LeadManagementGate action="delete">
                                <button
                                  onClick={() => handleDeleteClick(leadId)}
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete Lead"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </LeadManagementGate>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {
        (() => {
          const leadsTotalItems = Number(pagination?.totalLeads || leads.length || 0);
          const leadsTotalPages = Number(pagination?.totalPages || 1);
          const effectivePaginationLimit = pagination?.limit || filters.limit || 10;
          // Show pagination if there's more than one page
          const shouldShowPagination = !loading && leadsTotalPages > 1;
          const handleLeadsPageChange = (page) => {
            updatePage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          };



          return shouldShowPagination ? (
            <PaginationControls
              currentPage={pagination.currentPage || filters.page || 1}
              totalPages={leadsTotalPages}
              totalItems={leadsTotalItems}
              limit={effectivePaginationLimit}
              hasNextPage={pagination.hasNextPage !== false && (pagination.currentPage || 1) < leadsTotalPages}
              hasPrevPage={pagination.hasPrevPage !== false && (pagination.currentPage || 1) > 1}
              onPageChange={handleLeadsPageChange}
              onLimitChange={(limit) => handleItemsPerPageChange(limit)}
              limitOptions={[3, 10, 25, 50, 100]}
            />
          ) : null;
        })()
      }

      {/* Delete Confirmation Modal */}
      {
        showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
                <button
                  onClick={handleCancelDelete}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete this lead? This action cannot be undone.
                </p>
              </div>
              <div className="flex items-center justify-end space-x-3">
                <Button
                  className="btn-cancel"
                  onClick={handleCancelDelete}
                >
                  Cancel
                </Button>
                <LeadManagementGate action="delete">
                  <Button
                    variant="primary"
                    onClick={handleConfirmDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete
                  </Button>
                </LeadManagementGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Delete Confirmation Modal */}
      {
        showBulkDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] !mt-0">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Confirm Bulk Delete</h3>
                <button
                  onClick={() => !isDeletingBulk && setShowBulkDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isDeletingBulk}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete <strong>{selectedRows.size}</strong> leads? This action cannot be undone.
                </p>
                {isDeletingBulk && (
                  <div className="mt-4 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
                    <span className="ml-2 text-sm text-gray-600">Deleting leads...</span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end space-x-3">
                <Button
                  className="btn-cancel"
                  onClick={() => setShowBulkDeleteModal(false)}
                  disabled={isDeletingBulk}
                >
                  Cancel
                </Button>
                <LeadManagementGate action="delete">
                  <Button
                    variant="primary"
                    onClick={handleConfirmBulkDelete}
                    disabled={isDeletingBulk}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {isDeletingBulk ? 'Deleting...' : 'Delete All'}
                  </Button>
                </LeadManagementGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Auto Qualification Confirmation Modal */}
      {
        showAutoQualificationModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Run Auto Qualification</h3>
                <button
                  onClick={() => setShowAutoQualificationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isRunningAutoQualification}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  This will run qualification for all disqualified leads based on your active qualification criteria.
                  This process may take a while depending on the number of leads.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  className="btn-cancel"
                  onClick={() => setShowAutoQualificationModal(false)}
                  disabled={isRunningAutoQualification}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  icon={<Target className="h-4 w-4" />}
                  onClick={handleRunAutoQualification}
                  disabled={isRunningAutoQualification}
                  className="btn-create"
                >
                  {isRunningAutoQualification ? 'Starting...' : 'Run Qualification'}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Auto Assignment Confirmation Modal */}
      {
        showAutoAssignmentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Run Auto Assignment</h3>
                <button
                  onClick={() => setShowAutoAssignmentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isRunningAutoAssignment}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  This will run auto-assignment for all unassigned leads based on your active assignment rules.
                  This process may take a while depending on the number of leads.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  className="btn-cancel"
                  onClick={() => setShowAutoAssignmentModal(false)}
                  disabled={isRunningAutoAssignment}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  icon={isRunningAutoAssignment ? <RefreshCw className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                  onClick={handleRunAutoAssignment}
                  disabled={isRunningAutoAssignment}
                  className="btn-create"
                >
                  {isRunningAutoAssignment ? 'Running...' : 'Run Assignment'}
                </Button>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Assign Modal */}
      {
        showBulkAssignModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] !mt-0">
            <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Bulk Assign Leads</h3>
                <button
                  onClick={() => !isAssigningBulk && setShowBulkAssignModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={isAssigningBulk}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-4">
                  Select a user to assign <strong>{selectedRows.size}</strong> leads to:
                </p>

                <div className="relative">
                  <div className="flex items-center bg-gray-50 border border-gray-300 rounded px-3 py-2">
                    <Search className="h-4 w-4 text-gray-400 mr-2" />
                    <input
                      type="text"
                      placeholder="Search user..."
                      className="input-field text-sm"
                      value={bulkAssignSearchTerm}
                      onChange={(e) => {
                        setBulkAssignSearchTerm(e.target.value);
                        loadUsers(e.target.value, 'bulk');
                      }}
                    />
                  </div>

                  <div className="mt-2 max-h-48 overflow-y-auto border border-gray-200 rounded">
                    {loadingUsers ? (
                      <div className="p-2 text-center text-xs text-gray-500">Loading users...</div>
                    ) : bulkAssignUsers.length > 0 ? (
                      bulkAssignUsers.map(user => (
                        <div
                          key={user.id}
                          className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${bulkAssignee === user.id ? 'bg-blue-50 text-[#0B2033]' : 'text-gray-700'}`}
                          onClick={() => setBulkAssignee(user.id)}
                        >
                          <div className="font-medium">{user.name}</div>
                          <div className="text-xs text-gray-500">{user.email} - {user.role}</div>
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-center text-xs text-gray-500">No users found</div>
                    )}
                  </div>
                </div>

                {isAssigningBulk && (
                  <div className="mt-4 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0F2942]"></div>
                    <span className="ml-2 text-sm text-gray-600">Assigning leads...</span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3">
                <Button
                  className="btn-cancel"
                  onClick={() => setShowBulkAssignModal(false)}
                  disabled={isAssigningBulk}
                >
                  Cancel
                </Button>
                <LeadManagementGate action="update">
                  <Button
                    className="btn-create"
                    onClick={handleConfirmBulkAssign}
                    disabled={isAssigningBulk || !bulkAssignee}
                  >
                    {isAssigningBulk ? 'Assigning...' : 'Assign All'}
                  </Button>
                </LeadManagementGate>
              </div>
            </div>
          </div>
        )
      }

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSuccess={() => {
          // Refresh leads list after successful upload
          loadLeads(buildApiFilters());
        }}
        config={{
          title: 'Bulk Upload Leads',
          subtitle: 'Import multiple leads using an Excel file',
          templateEndpoint: '/crm/leads/bulk-upload/template',
          validateEndpoint: '/crm/leads/bulk-upload/validate',
          uploadEndpoint: '/crm/leads/bulk-upload',
          templateFilename: 'lead_bulk_upload_template.xlsx',
          dataKey: 'leads',
          instructions: {
            download: [
              <span><strong className="font-bold text-gray-900">Template Structure:</strong> Fill data in Sheet 1 ("Lead Upload"). Refer to Sheet 2 ("Dropdown Master Values") for all dropdown lists.</span>,
              <span><strong className="font-bold text-red-600">Mandatory Field:</strong> First Name *. Email or Phone is recommended for deduplication.</span>,
              <span><strong className="font-bold text-gray-900">Lead Sources:</strong> {masterSources.map(s => s.name).join(', ') || 'Cold Call, Website, Referral, LinkedIn, WhatsApp'}</span>,
              <span><strong className="font-bold text-gray-900">Current Stages (Status):</strong> {masterStatuses.map(s => s.name).join(', ') || 'New, Contacted, Qualified, Converted, Disqualified, Closed Won, Closed Lost'}</span>,
              <span><strong className="font-bold text-gray-900">Lead Temperatures:</strong> Hot, Warm, Cold</span>,
              <span><strong className="font-bold text-gray-900">Company Sizes:</strong> {masterCompanySizes.map(s => s.name).join(', ') || 'Startup (1-10 employees), Small (10-50 employees), Medium (50-200 employees), Large (200-1000 employees), Enterprise (1000+ employees)'}</span>,
              <span><strong className="font-bold text-gray-900">Industries (Name / Code):</strong> {masterIndustries.filter(i => i.isActive !== false).map(i => `${i.name}${i.industryCode ? ` (${i.industryCode})` : ''}`).join(', ') || 'Loading...'}</span>,
              <span><strong className="font-bold text-gray-900">Decision Makers:</strong> {masterDecisionMakers.filter(dm => dm.isActive !== false).map(dm => dm.name).join(', ') || 'Loading...'}</span>,
              <span><strong className="font-bold text-gray-900">Lead Categories:</strong> {masterCategories.filter(c => c.isActive !== false).map(c => c.name).join(', ') || 'Loading...'}</span>,
              <span><strong className="font-bold text-gray-900">Assigned To:</strong> Provide active user's registered Email or Employee ID number.</span>
            ],
            prepare: [
              <span><strong className="font-bold text-gray-900">Business Units:</strong> {masterBusinessUnits.map(u => u.name).join(', ') || 'Converiqo, Mobiloitte'}</span>,
              <span><strong className="font-bold text-gray-900">Lead Buckets:</strong> {masterLeadBuckets.map(b => b.name).join(', ') || 'Qualified Pipeline, Cold Leads'}</span>,
              <span><strong className="font-bold text-gray-900">Revenue Types:</strong> {masterRevenueTypes.map(r => r.name).join(', ') || 'Monthly, One time, One time + Monthly'}</span>,
              <span><strong className="font-bold text-gray-900">Last Active Channels:</strong> {masterLastActiveChannels.map(c => c.name).join(', ') || 'Phone Call, Email, Meeting, WhatsApp'}</span>,
              <span><strong className="font-bold text-gray-900">Regions:</strong> North, South, East, West, Central, North-East, North-West, South-East, South-West</span>,
              <span><strong className="font-bold text-gray-900">Timelines:</strong> Immediate (within 1 month), Short-term (1-3 months), Medium-term (3-6 months), Long-term (6+ months)</span>,
              <span><strong className="font-bold text-gray-900">Priority:</strong> 1 (Low), 2 (Medium), 3 (High)</span>,
              <span><strong className="font-bold text-gray-900">Document Types:</strong> {masterDocumentTypes.filter(dt => dt.isActive !== false).map(dt => dt.name).join(', ') || 'GST, PAN'}</span>,
              <span><strong className="font-bold text-gray-900">Multiple Documents:</strong> Use semicolon (;) under documentType, certificateNumber, and documentUrl (e.g. "GST;PAN").</span>,
              <span><strong className="font-bold text-gray-900">Update / Upsert:</strong> Providing an existing Lead ID along with followupType and details updates the lead and creates a new lead activity.</span>
            ]
          }
        }}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Leads"
        showDateRange
        scopedFormatExport={{
          onExport: handleCrmLeadsScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />

      {/* Assignment Rule Modal */}
      {
        showRuleModal && (
          <AssignmentRuleModal
            isOpen={showRuleModal}
            onClose={() => setShowRuleModal(false)}
            ruleToEdit={null} // Always create mode from here
            users={users}
            onSearchUsers={loadUsersForRuleModal}
            loadingUsers={loadingUsers}
            filteredUsers={filteredUsers}
            canModify={canModify}
            onSuccess={() => {
              // Rule created, strictly speaking we don't need to refresh leads list unless 
              // we want to reflect something immediately, but typically rules affect *future* assignments
              // or explicit auto-assignment runs.
              // We could fetch assignment rules count if we tracked it here, but we don't.
            }}
          />
        )
      }

      {/* Scoring Rule Modal */}
      {
        showScoringModal && (
          <ScoringRuleModal
            isOpen={showScoringModal}
            onClose={() => setShowScoringModal(false)}
            selectedScoring={selectedScoring}
            onSave={handleSaveScoring}
            isSaving={isSavingScoring}
            scoringFormData={scoringFormData}
            setScoringFormData={setScoringFormData}
            scoringErrors={scoringErrors}
            setScoringErrors={setScoringErrors}
          />
        )
      }

      {/* Qualification Criteria Modal */}
      {
        showCriteriaModal && (
          <QualificationCriteriaModal
            isOpen={showCriteriaModal}
            onClose={() => setShowCriteriaModal(false)}
            selectedCriteria={selectedCriteria}
            onSave={handleSaveCriteria}
            isSaving={isSavingCriteria}
            criteriaFormData={criteriaFormData}
            setCriteriaFormData={setCriteriaFormData}
            criteriaErrors={criteriaErrors}
            setCriteriaErrors={setCriteriaErrors}
          />
        )
      }

      {/* Convert Lead Modal */}
      <ConvertLeadModal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        lead={selectedLeadForConversion}
        onSubmit={handleConvertToOpportunity}
      />
    </div >
  );
};

export default LeadListFilters;
